import React, { useEffect, useState } from 'react';
import { FiEdit, FiFileText, FiPlus, FiRefreshCw, FiTrash2 } from 'react-icons/fi';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import { formatarData } from '../../utils/dateUtils';

const initialForm = {
  viagem_id: '',
  pousada_id: '',
  data_entrada: '',
  data_saida: '',
  qtd_hospedes: '',
  qtd_quartos: '',
  tipo_acomodacao: '',
  valor_total: '',
  sinal: '',
  saldo_restante: '',
  forma_pagamento: '',
  status: 'pendente',
  observacoes: '',
  clausulas_adicionais: '',
};

const FORMAS_PAGAMENTO_HOSPEDAGEM = ['PIX', 'CARTÃO', 'TRANSFERÊNCIA BANCÁRIA'];

const normalizarFormaPagamento = (valor) => {
  const texto = String(valor || '').trim().toUpperCase();
  if (texto === 'PIX') return 'PIX';
  if (texto === 'CARTÃO' || texto === 'CARTAO') return 'CARTÃO';
  if (texto === 'TRANSFERÊNCIA BANCÁRIA' || texto === 'TRANSFERENCIA BANCARIA') return 'TRANSFERÊNCIA BANCÁRIA';
  return '';
};

const exibirFormaPagamento = (valor) => normalizarFormaPagamento(valor) || valor || '-';

const ContratosHospedagem = () => {
  const [loading, setLoading] = useState(true);
  const [contratos, setContratos] = useState([]);
  const [viagens, setViagens] = useState([]);
  const [pousadas, setPousadas] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editando, setEditando] = useState(null);
  const [formData, setFormData] = useState(initialForm);

  useEffect(() => {
    fetchDados();
  }, []);

  useEffect(() => {
    const valorTotal = Number(formData.valor_total || 0);
    const sinal = Number(formData.sinal || 0);
    setFormData((prev) => ({ ...prev, saldo_restante: (valorTotal - sinal).toFixed(2) }));
  }, [formData.valor_total, formData.sinal]);

  const fetchDados = async () => {
    try {
      setLoading(true);
      const [contratosRes, viagensRes, pousadasRes] = await Promise.all([
        api.get('/api/contratos-hospedagem'),
        api.get('/api/viagens'),
        api.get('/api/pousadas'),
      ]);
      setContratos(contratosRes.data || []);
      setViagens(viagensRes.data || []);
      setPousadas(pousadasRes.data || []);
    } catch (error) {
      toast.error('Erro ao carregar contratos de hospedagem');
    } finally {
      setLoading(false);
    }
  };

  const abrirNovo = () => {
    setEditando(null);
    setFormData(initialForm);
    setModalOpen(true);
  };

  const abrirEdicao = (contrato) => {
    setEditando(contrato);
    setFormData({
      viagem_id: contrato.viagem_id || '',
      pousada_id: contrato.pousada_id || '',
      data_entrada: contrato.data_entrada || '',
      data_saida: contrato.data_saida || '',
      qtd_hospedes: contrato.qtd_hospedes || '',
      qtd_quartos: contrato.qtd_quartos || '',
      tipo_acomodacao: contrato.tipo_acomodacao || '',
      valor_total: contrato.valor_total || '',
      sinal: contrato.sinal || '',
      saldo_restante: contrato.saldo_restante || '',
      forma_pagamento: normalizarFormaPagamento(contrato.forma_pagamento),
      status: contrato.status || 'pendente',
      observacoes: contrato.observacoes || '',
      clausulas_adicionais: contrato.clausulas_adicionais || '',
    });
    setModalOpen(true);
  };

  const fecharModal = () => {
    setModalOpen(false);
    setEditando(null);
    setFormData(initialForm);
  };

  const salvarContrato = async (e) => {
    e.preventDefault();

    if (!formData.viagem_id) return toast.error('Viagem é obrigatória');
    if (!formData.pousada_id) return toast.error('Pousada é obrigatória');
    if (!formData.data_entrada) return toast.error('Data de entrada é obrigatória');
    if (!formData.data_saida) return toast.error('Data de saída é obrigatória');
    if (new Date(`${formData.data_saida}T00:00:00`) < new Date(`${formData.data_entrada}T00:00:00`)) {
      return toast.error('Data de saída não pode ser anterior à data de entrada');
    }
    if (!formData.qtd_hospedes || Number(formData.qtd_hospedes) <= 0) {
      return toast.error('Quantidade de hóspedes é obrigatória');
    }
    if (!formData.valor_total || Number(formData.valor_total) <= 0) {
      return toast.error('Valor total é obrigatório');
    }

    try {
      if (editando) {
        await api.put(`/api/contratos-hospedagem/${editando.id}`, formData);
        toast.success('Contrato de hospedagem atualizado com sucesso');
      } else {
        await api.post('/api/contratos-hospedagem', formData);
        toast.success('Contrato de hospedagem criado com sucesso');
      }
      fecharModal();
      fetchDados();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao salvar contrato de hospedagem');
    }
  };

  const excluirContrato = async (id) => {
    if (!window.confirm('Deseja excluir este contrato de hospedagem?')) return;
    try {
      await api.delete(`/api/contratos-hospedagem/${id}`);
      toast.success('Contrato de hospedagem excluído com sucesso');
      fetchDados();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao excluir contrato de hospedagem');
    }
  };

  const gerarPdfContrato = async (id) => {
    try {
      const response = await api.get(`/api/contratos-hospedagem/${id}/pdf`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      link.download = `contrato_hospedagem_${id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('PDF gerado com sucesso');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao gerar PDF do contrato');
    }
  };

  const statusClass = (status) => {
    if (status === 'confirmado') return 'bg-green-100 text-green-800';
    if (status === 'cancelado') return 'bg-red-100 text-red-800';
    return 'bg-yellow-100 text-yellow-800';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Contratos de Hospedagem</h1>
          <p className="text-white/70 mt-1">Gerencie contratos vinculados a viagem e pousada</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={fetchDados}
            className="flex items-center gap-2 px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 transition-colors"
          >
            <FiRefreshCw size={18} />
            Atualizar
          </button>
          <button
            onClick={abrirNovo}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <FiPlus size={18} />
            Novo Contrato
          </button>
        </div>
      </div>

      <div className="bg-sky-50 rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Viagem</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pousada</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Período</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hóspedes</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Valor Total</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {contratos.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
                    Nenhum contrato de hospedagem encontrado
                  </td>
                </tr>
              ) : (
                contratos.map((contrato) => (
                  <tr key={contrato.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{contrato.viagem_titulo || 'N/A'}</div>
                      <div className="text-sm text-gray-500">{contrato.viagem_destino || '-'}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{contrato.pousada_nome || 'N/A'}</div>
                      <div className="text-sm text-gray-500">
                        {contrato.pousada_cidade || '-'}
                        {contrato.pousada_estado ? `/${contrato.pousada_estado}` : ''}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      {formatarData(contrato.data_entrada)} até {formatarData(contrato.data_saida)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">{contrato.qtd_hospedes}</td>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">
                      R$ {Number(contrato.valor_total || 0).toFixed(2)}
                      <div className="text-xs text-gray-500 mt-1">Pagamento: {exibirFormaPagamento(contrato.forma_pagamento)}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${statusClass(contrato.status)}`}>
                        {contrato.status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <button onClick={() => gerarPdfContrato(contrato.id)} className="text-blue-600 hover:text-blue-900" title="Gerar PDF">
                          <FiFileText size={18} />
                        </button>
                        <button onClick={() => abrirEdicao(contrato)} className="text-sky-600 hover:text-sky-900" title="Editar">
                          <FiEdit size={18} />
                        </button>
                        <button onClick={() => excluirContrato(contrato.id)} className="text-red-600 hover:text-red-900" title="Excluir">
                          <FiTrash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold text-gray-900">{editando ? 'Editar Contrato de Hospedagem' : 'Novo Contrato de Hospedagem'}</h2>
                <button onClick={fecharModal} className="text-gray-400 hover:text-gray-600">
                  ✕
                </button>
              </div>

              <form onSubmit={salvarContrato} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Viagem *</label>
                    <select
                      value={formData.viagem_id}
                      onChange={(e) => setFormData({ ...formData, viagem_id: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 bg-white text-gray-800"
                      required
                    >
                      <option value="">Selecione...</option>
                      {viagens.map((v) => (
                        <option key={v.id} value={v.id}>
                          {v.titulo}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Pousada *</label>
                    <select
                      value={formData.pousada_id}
                      onChange={(e) => setFormData({ ...formData, pousada_id: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 bg-white text-gray-800"
                      required
                    >
                      <option value="">Selecione...</option>
                      {pousadas.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.nome} - {p.cidade}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Data de Entrada *</label>
                    <input
                      type="date"
                      value={formData.data_entrada}
                      onChange={(e) => setFormData({ ...formData, data_entrada: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 bg-white text-gray-800"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Data de Saída *</label>
                    <input
                      type="date"
                      value={formData.data_saida}
                      onChange={(e) => setFormData({ ...formData, data_saida: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 bg-white text-gray-800"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Qtd. Hóspedes *</label>
                    <input
                      type="number"
                      min="1"
                      value={formData.qtd_hospedes}
                      onChange={(e) => setFormData({ ...formData, qtd_hospedes: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 bg-white text-gray-800"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Qtd. Quartos</label>
                    <input
                      type="number"
                      min="0"
                      value={formData.qtd_quartos}
                      onChange={(e) => setFormData({ ...formData, qtd_quartos: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 bg-white text-gray-800"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de Acomodação</label>
                    <input
                      type="text"
                      value={formData.tipo_acomodacao}
                      onChange={(e) => setFormData({ ...formData, tipo_acomodacao: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 bg-white text-gray-800"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Forma de Pagamento</label>
                    <select
                      value={formData.forma_pagamento}
                      onChange={(e) => setFormData({ ...formData, forma_pagamento: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 bg-white text-gray-800"
                    >
                      <option value="">Selecione...</option>
                      {FORMAS_PAGAMENTO_HOSPEDAGEM.map((opcao) => (
                        <option key={opcao} value={opcao}>
                          {opcao}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Valor Total (R$) *</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.valor_total}
                      onChange={(e) => setFormData({ ...formData, valor_total: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 bg-white text-gray-800"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Sinal (R$)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.sinal}
                      onChange={(e) => setFormData({ ...formData, sinal: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 bg-white text-gray-800"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Saldo Restante (R$)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.saldo_restante}
                      onChange={(e) => setFormData({ ...formData, saldo_restante: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 bg-white text-gray-800"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 bg-white text-gray-800"
                    >
                      <option value="pendente">Pendente</option>
                      <option value="confirmado">Confirmado</option>
                      <option value="cancelado">Cancelado</option>
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Observações</label>
                    <textarea
                      rows="3"
                      value={formData.observacoes}
                      onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 bg-white text-gray-800"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Cláusulas Adicionais</label>
                    <textarea
                      rows="4"
                      value={formData.clausulas_adicionais}
                      onChange={(e) => setFormData({ ...formData, clausulas_adicionais: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 bg-white text-gray-800"
                    />
                  </div>
                </div>

                <div className="flex gap-3 justify-end">
                  <button type="button" onClick={fecharModal} className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                    Cancelar
                  </button>
                  <button type="submit" className="px-6 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700">
                    {editando ? 'Atualizar' : 'Criar'} Contrato
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContratosHospedagem;
